/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Boton } from "../../../shared";
import { accessAPI } from "../../../shared";

// Listado de productos leídos de la plantilla Excel para que el colaborador
// los revise, corrija o descarte antes de agregarlos al proceso. Cada producto
// se crea con el mismo endpoint que el formulario de alta manual.
export default function CargaMasivaProductos({
  productosIniciales,
  camposPropios,
  pasoId,
  agregarProductoAProceso,
  alTerminar,
}) {
  // Los campos autocompletados por script no se completan a mano
  const campos = camposPropios.filter((campo) => !campo.autocompletarScript);

  const [productos, setProductos] = useState(productosIniciales);
  const [loaderAgregar, setLoaderAgregar] = useState(false);
  const [progreso, setProgreso] = useState(null);

  // Valida los campos de un producto, devuelve un objeto {campoId: mensaje}
  function erroresDeProducto(producto) {
    const errores = {};
    for (const campo of campos) {
      const valor = producto.campospropios[campo.id];
      const vacio =
        valor === undefined || valor === null || String(valor).trim() === "";
      if (vacio) {
        if (campo.obligatoriobool) {
          errores[campo.id] = "Este campo es obligatorio";
        }
        continue;
      }
      if (campo.opciones?.length) {
        if (campo.opciones.indexOf(valor) === -1) {
          errores[campo.id] =
            `El valor "${valor}" de la planilla no está entre las opciones`;
        }
      } else if (campo.tipo === "int") {
        // eslint-disable-next-line eqeqeq
        if (isNaN(valor) || parseInt(valor) != valor) {
          errores[campo.id] = "Debe ser un número entero";
        }
      } else if (campo.tipo === "float") {
        const normalizado = String(valor).replace(",", ".");
        // eslint-disable-next-line eqeqeq
        if (isNaN(normalizado) || parseFloat(normalizado) != normalizado) {
          errores[campo.id] = "Debe ser un número";
        }
      }
    }
    return errores;
  }

  const erroresPorProducto = productos.map(erroresDeProducto);
  const hayErrores = erroresPorProducto.some(
    (errores) => Object.keys(errores).length > 0
  );

  function actualizarCampo(indice, campoId, valor) {
    setProductos((actuales) =>
      actuales.map((producto, i) =>
        i === indice
          ? {
              ...producto,
              campospropios: { ...producto.campospropios, [campoId]: valor },
            }
          : producto
      )
    );
  }

  function eliminarProducto(indice) {
    setProductos((actuales) => actuales.filter((_, i) => i !== indice));
  }

  // Envuelve el POST de creación en una promesa para poder crear en secuencia
  function crearProducto(producto) {
    return new Promise((resolve) => {
      accessAPI(
        "POST",
        `colab/actividad/${pasoId}/producto`,
        { campospropios: producto.campospropios },
        (respuesta) => resolve({ ok: true, respuesta }),
        (respuesta) => resolve({ ok: false, respuesta })
      );
    });
  }

  // Crea los productos de a uno; los que fallan quedan en el listado con su error
  async function agregarProductos() {
    setLoaderAgregar(true);
    const fallidos = [];
    let agregados = 0;
    for (let i = 0; i < productos.length; i++) {
      setProgreso(`Agregando producto ${i + 1} de ${productos.length}`);
      const resultado = await crearProducto(productos[i]);
      if (resultado.ok) {
        agregarProductoAProceso(resultado.respuesta);
        agregados = agregados + 1;
      } else {
        fallidos.push({
          ...productos[i],
          error:
            resultado.respuesta?.[0]?.msg ||
            "Ocurrió un error, intente nuevamente más tarde",
        });
      }
    }
    setLoaderAgregar(false);
    setProgreso(null);
    setProductos(fallidos);
    alTerminar(agregados, fallidos.length);
  }

  return (
    <div>
      <h1>Agregar productos desde planilla</h1>
      <p css={{ color: "var(--grisTextos)" }}>
        Revisá los productos leídos de la planilla. Podés corregir campos o
        eliminar productos antes de agregarlos al proceso.
      </p>
      {productos.map((producto, indice) => {
        const errores = erroresPorProducto[indice];
        return (
          <div
            key={`producto-plantilla-${producto.fila}-${indice}`}
            css={{
              border: "1px solid var(--surface-border)",
              borderRadius: "6px",
              padding: "1em",
              marginBottom: "1em",
            }}
          >
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.75em",
              }}
            >
              <strong>
                Producto {indice + 1}
                <span
                  css={{
                    color: "var(--grisTextos)",
                    fontWeight: "normal",
                    marginLeft: "0.5em",
                    fontSize: "0.85rem",
                  }}
                >
                  (fila {producto.fila} de la planilla)
                </span>
              </strong>
              <Boton
                texto
                icono
                chico
                rojo
                disabled={loaderAgregar}
                callback={() => eliminarProducto(indice)}
              >
                <i className="pi pi-trash"></i>
              </Boton>
            </div>
            {producto.error && (
              <div
                css={{
                  color: "var(--red-500)",
                  fontSize: "0.85rem",
                  marginBottom: "0.75em",
                }}
              >
                {producto.error}
              </div>
            )}
            {campos.map((campo) => {
              const valor = producto.campospropios[campo.id] ?? "";
              const error = errores[campo.id];
              const etiqueta = campo.unidades
                ? `${campo.etiqueta} (${campo.unidades})`
                : campo.etiqueta;
              return (
                <div key={campo.id} css={{ marginBottom: "0.75em" }}>
                  <label
                    css={{
                      display: "block",
                      fontSize: "0.85rem",
                      marginBottom: "0.25em",
                    }}
                  >
                    {etiqueta}
                    {campo.obligatoriobool && " *"}
                  </label>
                  {campo.opciones?.length ? (
                    <Dropdown
                      value={valor || null}
                      options={campo.opciones.map((opcion) => ({
                        label: opcion,
                        value: opcion,
                      }))}
                      onChange={(e) =>
                        actualizarCampo(indice, campo.id, e.value)
                      }
                      className={error ? "p-invalid" : ""}
                      disabled={loaderAgregar}
                      css={{ width: "100%" }}
                    />
                  ) : (
                    <InputText
                      value={valor}
                      onChange={(e) =>
                        actualizarCampo(indice, campo.id, e.target.value)
                      }
                      className={error ? "p-invalid" : ""}
                      disabled={loaderAgregar}
                      css={{ width: "100%" }}
                    />
                  )}
                  {error && (
                    <small css={{ color: "var(--red-500)" }}>{error}</small>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      {productos.length === 0 && (
        <p css={{ color: "var(--grisTextos)" }}>
          No quedan productos para agregar.
        </p>
      )}
      <div css={{ marginTop: "1.5em" }}>
        {progreso && (
          <div css={{ marginBottom: "0.5em", color: "var(--grisTextos)" }}>
            {progreso}
          </div>
        )}
        {hayErrores && (
          <div
            css={{
              marginBottom: "0.5em",
              color: "var(--red-500)",
              fontSize: "0.85rem",
            }}
          >
            Corregí los errores marcados antes de agregar los productos.
          </div>
        )}
        <Boton
          verde
          ancho
          loader={loaderAgregar}
          disabled={hayErrores || productos.length === 0}
          callback={agregarProductos}
        >
          {`Agregar ${productos.length} producto${
            productos.length === 1 ? "" : "s"
          } al proceso`}
        </Boton>
      </div>
    </div>
  );
}
